import React, { useState, useEffect, useRef } from 'react';
import { Camera, MapPin, CheckCircle, AlertCircle, Users, Clock, Shield, X } from 'lucide-react';
import { api } from '../utils/api';
import { getCameraConstraints } from '../utils/helpers';
import Swal from 'sweetalert2';
import * as faceapi from 'face-api.js';

const FaceScanner = ({ user }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const captureCanvasRef = useRef(null);
  const streamRef = useRef(null);
  const boxRef = useRef(null); 

  const [isScanning, setIsScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date()); 
  
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [isWarmingUp, setIsWarmingUp] = useState(false);
  const [location, setLocation] = useState({ loading: true });
  const [wfhMode, setWfhMode] = useState(false);
  const [isWfhChecked, setIsWfhChecked] = useState(false); 
  
  const [pendingConfirmation, setPendingConfirmation] = useState(null);
  const [isDetecting, setIsDetecting] = useState(false); 
  const [readyCountdown, setReadyCountdown] = useState(null); 
  
  // [เพิ่ม] State สำหรับเก็บข้อความแจ้งเตือนบนหน้าจอกล้อง
  const [scanMessage, setScanMessage] = useState(null);
  
  const CURRENT_VERSION = 'v4.3.0';

  useEffect(() => {
    const loadModels = async () => {
      try {
        const MODEL_URL = 'https://cdn.jsdelivr.net/gh/justadudewhohacks/face-api.js@master/weights';
        await faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL);
        setModelsLoaded(true);
      } catch (error) {
        console.error("Error loading face-api models:", error);
        Swal.fire('ข้อผิดพลาด', 'ไม่สามารถโหลดโมเดล AI ตรวจจับใบหน้าจาก CDN ได้', 'error');
      }
    };
    
    loadModels();

    setTimeout(() => {
      setIsWfhChecked(true);
      setWfhMode(false); 
    }, 500);

    setTimeout(() => {
      setLocation({
        latitude: 13.756331, 
        longitude: 100.501762,
        accuracy: 15,
        loading: false,
        error: false
      });
    }, 2000);
  }, []);

  useEffect(() => {
    if (readyCountdown === null || readyCountdown <= 0) return;
    
    const timer = setTimeout(() => {
      setReadyCountdown(prev => prev - 1);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [readyCountdown]);

  useEffect(() => {
    const videoElement = videoRef.current;
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      if (videoElement) {
        videoElement.pause();
        videoElement.srcObject = null;
        videoElement.removeAttribute('src');
        videoElement.load();
      }
    };
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (modelsLoaded && !isWarmingUp && !location.loading && isWfhChecked && !cameraActive && !pendingConfirmation && !result) {
      startCamera();
    }
  }, [modelsLoaded, isWarmingUp, location, isWfhChecked, cameraActive, pendingConfirmation, result]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia(getCameraConstraints());
      streamRef.current = stream;

      if (!videoRef.current) return;

      videoRef.current.srcObject = stream;
      setCameraActive(true);
      setResult(null);
      setPendingConfirmation(null);
      setScanMessage(null); // เคลียร์ข้อความตอนเปิดกล้องใหม่
      
    } catch (error) {
      console.error('Camera error:', error);
      Swal.fire('ข้อผิดพลาด', 'ไม่สามารถเข้าถึงกล้องได้', 'error');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.srcObject = null;
      videoRef.current.removeAttribute('src');
      videoRef.current.load();
    }
    
    setCameraActive(false);
    setIsScanning(false);
    setReadyCountdown(null);
  };

  const captureImage = () => {
    const video = videoRef.current;
    const captureCanvas = captureCanvasRef.current;
    
    if (!video || !captureCanvas) return null;
    
    captureCanvas.width = video.videoWidth;
    captureCanvas.height = video.videoHeight;
    
    const ctx = captureCanvas.getContext('2d');
    ctx.save();
    ctx.scale(-1, 1);
    ctx.drawImage(video, -captureCanvas.width, 0, captureCanvas.width, captureCanvas.height);
    ctx.restore();
    
    return captureCanvas.toDataURL('image/jpeg', 0.8);
  };

  const handleManualCapture = async () => {
    const video = videoRef.current;
    const box = boxRef.current;
    
    if (!video || !box) return;

    setIsDetecting(true); 
    setScanMessage(null); // เคลียร์ข้อความก่อนเริ่มสแกนใหม่

    try {
      const detections = await faceapi.detectAllFaces(video, new faceapi.TinyFaceDetectorOptions());

      // [แก้ไข] เปลี่ยนจาก Swal เป็นการตั้งค่า State ข้อความแทน
      if (detections.length === 0) {
        setIsDetecting(false);
        setScanMessage('ไม่พบใบหน้า กรุณามองกล้อง!');
        setTimeout(() => setScanMessage(null), 3000); // ให้ข้อความหายไปเองใน 3 วิ
        return; 
      }

      const videoRect = video.getBoundingClientRect();
      const boxRect = box.getBoundingClientRect();

      const scaleX = video.videoWidth / videoRect.width;
      const scaleY = video.videoHeight / videoRect.height;

      const margin = 20; 
      const boxLeft = ((boxRect.left - videoRect.left) * scaleX) - margin;
      const boxRight = ((boxRect.right - videoRect.left) * scaleX) + margin;
      const boxTop = ((boxRect.top - videoRect.top) * scaleY) - margin;
      const boxBottom = ((boxRect.bottom - videoRect.top) * scaleY) + margin;

      const face = detections[0].box;
      const faceCenterX = face.x + (face.width / 2);
      const faceCenterY = face.y + (face.height / 2);

      const isInsideBox = 
        faceCenterX >= boxLeft && 
        faceCenterX <= boxRight && 
        faceCenterY >= boxTop && 
        faceCenterY <= boxBottom;

      // [แก้ไข] เปลี่ยนจาก Swal เป็นการตั้งค่า State ข้อความแทน
      if (!isInsideBox) {
        setIsDetecting(false);
        setScanMessage('กรุณาให้ใบหน้าอยู่ในกรอบ!');
        setTimeout(() => setScanMessage(null), 3000); // ให้ข้อความหายไปเองใน 3 วิ
        return;
      }

      const image = captureImage();
      if (!image) {
        setIsDetecting(false);
        return;
      }

      stopCamera();
      
      const currentHour = new Date().getHours();
      const action = currentHour < 12 ? 'check_in' : 'check_out';

      const mockEmployee = user || {
          employee_id: 'EMP-001',
          first_name: 'ผู้ใช้งาน',
          last_name: 'ทั่วไป',
          position: 'พนักงาน',
          department: 'ส่วนกลาง'
      };

      setPendingConfirmation({
        employee: mockEmployee,
        capturedImage: image,
        action: action,
        confidence: `${Math.round(detections[0].score * 100)}%`,
        timestamp: new Date().toISOString()
      });
      
    } catch (error) {
      console.error("Face detection error:", error);
      Swal.fire('ข้อผิดพลาด', 'ระบบตรวจจับใบหน้าขัดข้อง', 'error');
    } finally {
      setIsDetecting(false);
    }
  };

  const confirmAttendance = async () => {
    if (!pendingConfirmation) return;
    setIsScanning(true);
    
    try {
      const requestData = {
        captured_image: pendingConfirmation.capturedImage,
        employee_id: pendingConfirmation.employee.employee_id,
        confirmed: true,
        location: location 
      };
      
      const data = await api.recognizeFace(requestData);
      
      setResult(data);
      setPendingConfirmation(null);
      
    } catch (error) {
      setResult({ 
        success: false, 
        error: error.message || 'เกิดข้อผิดพลาดในการบันทึกข้อมูล'
      });
      setPendingConfirmation(null);
    } finally {
      setIsScanning(false);
    }
  };

  const cancelAndRescan = () => {
    setPendingConfirmation(null);
    setResult(null);
    startCamera();
  };

  const isSystemPreparing = !modelsLoaded || isWarmingUp || location.loading || !isWfhChecked;
  const isButtonReady = readyCountdown === 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      
      {pendingConfirmation && !result && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-300">
          <div className="bg-white rounded-3xl max-w-md w-full shadow-2xl animate-in zoom-in duration-300">
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-6 rounded-t-3xl">
              <div className="text-center">
                <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3 backdrop-blur-sm">
                  <Users className="h-10 w-10 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-1">ยืนยันตัวตน</h2>
                <p className="text-blue-100 text-sm">กรุณาตรวจสอบข้อมูลก่อนบันทึก</p>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-2xl p-5">
                <div className="text-center mb-4">
                  <div className="inline-block bg-white rounded-2xl px-4 py-2 shadow-sm mb-3">
                    <p className="text-3xl font-bold text-gray-900">
                      {pendingConfirmation.employee.first_name} {pendingConfirmation.employee.last_name}
                    </p>
                  </div>
                  <div className="flex items-center justify-center space-x-2 text-sm text-gray-600">
                    <span>{pendingConfirmation.employee.position}</span>
                    {pendingConfirmation.employee.department && (
                      <>
                        <span>•</span>
                        <span>{pendingConfirmation.employee.department}</span>
                      </>
                    )}
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="bg-white rounded-xl p-3 flex items-center justify-between shadow-sm">
                    <div className="flex items-center space-x-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        pendingConfirmation.action === 'check_in' ? 'bg-green-100' : 'bg-orange-100'
                      }`}>
                        {pendingConfirmation.action === 'check_in' ? (
                          <span className="text-lg">🟢</span>
                        ) : (
                          <span className="text-lg">🟠</span>
                        )}
                      </div>
                      <span className="text-sm text-gray-600 font-medium">ประเภท</span>
                    </div>
                    <span className={`font-bold text-lg ${
                      pendingConfirmation.action === 'check_in' ? 'text-green-600' : 'text-orange-600'
                    }`}>
                      {pendingConfirmation.action === 'check_in' ? 'เข้างาน' : 'ออกงาน'}
                    </span>
                  </div>

                  <div className="bg-white rounded-xl p-3 flex items-center justify-between shadow-sm">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <Clock className="h-4 w-4 text-blue-600" />
                      </div>
                      <span className="text-sm text-gray-600 font-medium">เวลา</span>
                    </div>
                    <span className="font-bold text-gray-900">
                      {new Date(pendingConfirmation.timestamp).toLocaleTimeString('th-TH', {
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit'
                      })}
                    </span>
                  </div>

                  {pendingConfirmation.confidence && (
                    <div className="bg-white rounded-xl p-3 flex items-center justify-between shadow-sm">
                      <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                          <Shield className="h-4 w-4 text-purple-600" />
                        </div>
                        <span className="text-sm text-gray-600 font-medium">ความมั่นใจของ AI</span>
                      </div>
                      <span className="font-bold text-green-600">
                        {pendingConfirmation.confidence}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 pt-2">
                <button
                  onClick={cancelAndRescan}
                  disabled={isScanning}
                  className="bg-gradient-to-r from-red-500 to-orange-600 text-white py-4 px-4 rounded-2xl font-bold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 disabled:opacity-50 flex flex-col items-center justify-center space-y-1"
                >
                  <X className="h-7 w-7" />
                  <span className="text-sm">ไม่ใช่ฉัน</span>
                </button>
                <button
                  onClick={confirmAttendance}
                  disabled={isScanning}
                  className="bg-gradient-to-r from-green-500 to-emerald-600 text-white py-4 px-4 rounded-2xl font-bold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 disabled:opacity-50 flex flex-col items-center justify-center space-y-1"
                >
                  {isScanning ? (
                    <>
                      <div className="w-7 h-7 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span className="text-sm">กำลังบันทึก...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-7 w-7" />
                      <span className="text-sm">ยืนยัน</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-md mx-auto px-4 py-6 space-y-6">
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 text-center shadow-lg border border-white/20">
            <Shield className={`h-6 w-6 mx-auto mb-2 ${modelsLoaded && !isWarmingUp ? 'text-green-500' : 'text-gray-400'}`} />
            <div className="text-xs font-medium text-gray-600">AI Model</div>
            <div className={`text-sm font-bold ${modelsLoaded && !isWarmingUp ? 'text-green-600' : 'text-gray-500'}`}>
              {!modelsLoaded ? 'Loading...' : 'Ready'}
            </div>
          </div>
          
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 text-center shadow-lg border border-white/20">
            <MapPin className={`h-6 w-6 mx-auto mb-2 ${wfhMode ? 'text-indigo-500' : (!location.loading ? 'text-blue-500' : 'text-gray-400')}`} />
            <div className="text-xs font-medium text-gray-600">Location</div>
            <div className={`text-sm font-bold ${wfhMode ? 'text-indigo-600' : (!location.loading ? 'text-blue-600' : 'text-gray-500')}`}>
              {wfhMode ? 'Active' : (location.loading ? 'Finding...' : 'Ready')}
            </div>
          </div>
          
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 text-center shadow-lg border border-white/20">
            <Clock className="h-6 w-6 mx-auto mb-2 text-indigo-500" />
            <div className="text-xs font-medium text-gray-600">Time</div>
            <div className="text-sm font-bold text-indigo-600">
              {currentTime.toLocaleTimeString('th-TH', { 
                hour: '2-digit', 
                minute: '2-digit', 
                second: '2-digit' 
              })}
            </div>
          </div>
        </div>

        <div className="bg-white/90 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20 overflow-hidden relative">
          <div className="relative bg-black rounded-t-3xl overflow-hidden" style={{ aspectRatio: '1/1' }}>
            
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              className="w-full h-full object-cover"
              style={{ 
                display: cameraActive ? 'block' : 'none',
                transform: 'scaleX(-1)',
                objectPosition: 'center top'
              }}
              onLoadedMetadata={() => {
                if (videoRef.current) {
                  videoRef.current.play();
                  setReadyCountdown(3);
                  if(modelsLoaded) {
                     faceapi.detectAllFaces(videoRef.current, new faceapi.TinyFaceDetectorOptions()).catch(e => console.log("Warm up bypassed", e));
                  }
                }
              }}
            />
            
            <canvas ref={captureCanvasRef} style={{ display: 'none' }} />
            
            {!cameraActive && !result && !pendingConfirmation && (
              <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-b from-gray-800 to-gray-900">
                <div className="text-center text-white">
                  <div className="relative w-24 h-24 mx-auto mb-6">
                    <div className="w-full h-full bg-white/10 rounded-full flex items-center justify-center backdrop-blur-sm">
                      <Camera className="h-12 w-12 text-white/70" />
                    </div>
                    {isSystemPreparing && (
                      <div className="absolute -inset-2 border-4 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                    )}
                  </div>
                  <h3 className="text-xl font-bold mb-2">
                    {isSystemPreparing ? 'กำลังเตรียมระบบ...' : 'พร้อมสแกนใบหน้า'}
                  </h3>
                  <p className="text-gray-300">
                    {!modelsLoaded ? 'กำลังโหลด Face API Models...' :
                     (!isWfhChecked) ? 'กำลังเช็คโหมด WFH...' :
                     (location.loading) ? 'กำลังค้นหาพิกัด GPS...' :
                     'กำลังเปิดกล้องอัตโนมัติ...'
                    }
                  </p>
                </div>
              </div>
            )}

            {cameraActive && !isScanning && (
              <div className="absolute inset-0">
                <div className="absolute top-0 left-0 right-0 bottom-0 flex flex-col items-center pt-12 pointer-events-none">
                  <div ref={boxRef} className="w-2/5 h-3/6 border-2 border-white/50 rounded-3xl relative mb-4">
                    <div className="absolute -top-1 -left-1 w-6 h-6 border-l-4 border-t-4 border-green-400 rounded-tl-lg"></div>
                    <div className="absolute -top-1 -right-1 w-6 h-6 border-r-4 border-t-4 border-green-400 rounded-tr-lg"></div>
                    <div className="absolute -bottom-1 -left-1 w-6 h-6 border-l-4 border-b-4 border-green-400 rounded-bl-lg"></div>
                    <div className="absolute -bottom-1 -right-1 w-6 h-6 border-r-4 border-b-4 border-green-400 rounded-br-lg"></div>
                  </div>
                  
                  {/* [แก้ไข] แสดงข้อความเมื่อมี Error เท่านั้น โดยใช้คลาสที่เน้นสีแดงนิดหน่อยให้ดูเป็นการแจ้งเตือน */}
                  {scanMessage && (
                    <div className="bg-red-500/80 backdrop-blur-sm text-white px-4 py-2 rounded-full font-medium text-sm shadow-lg border border-red-400 animate-in zoom-in duration-200">
                      {scanMessage}
                    </div>
                  )}
                </div>
                
                <div className="absolute bottom-6 left-0 right-0 flex justify-center z-10 px-6">
                  <button
                    onClick={handleManualCapture}
                    disabled={!isButtonReady || isDetecting}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 rounded-full shadow-[0_0_15px_rgba(37,99,235,0.5)] flex items-center justify-center space-x-2 transition-transform transform hover:scale-105 active:scale-95 disabled:opacity-70 disabled:transform-none disabled:bg-gray-500 disabled:shadow-none"
                  >
                    {!isButtonReady ? (
                       <>
                         <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                         <span className="text-lg">เตรียมระบบ... {readyCountdown}</span>
                       </>
                    ) : isDetecting ? (
                       <>
                         <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                         <span className="text-lg">กำลังตรวจสอบ...</span>
                       </>
                    ) : (
                       <>
                         <Camera className="w-6 h-6" />
                         <span className="text-lg">ลงเวลา</span>
                       </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>

          {result && (
            <div className="p-6">
              <div className="space-y-4">
                <div className={`p-6 rounded-2xl ${result.success ? 'bg-green-50 border-2 border-green-200' : 'bg-red-50 border-2 border-red-200'}`}>
                  <div className={`flex flex-col items-center justify-center text-center ${result.success ? 'text-green-800' : 'text-red-800'}`}>
                    <div className="flex items-center justify-center space-x-3 w-full">
                      {result.success ? (
                        <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
                          <CheckCircle className="h-7 w-7 text-white" />
                        </div>
                      ) : (
                        <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
                          <AlertCircle className="h-7 w-7 text-white" />
                        </div>
                      )}
                      <p className="text-xl font-bold">{result.message || result.error}</p>
                    </div>
                    
                    <div className="w-full mt-4">
                      {result.success && result.employee && (
                        <div className="space-y-1.5 flex flex-col items-center">
                          <div className="flex items-center justify-center space-x-2">
                            <Users className="h-4 w-4" />
                            <span className="font-medium">{result.employee.first_name} {result.employee.last_name}</span>
                          </div>
                          <div className="flex items-center justify-center space-x-2">
                            <div className={`w-3 h-3 rounded-full ${result.action === 'check_in' ? 'bg-blue-500' : 'bg-orange-500'}`}></div>
                            <span className="text-sm">{result.action === 'check_in' ? 'เข้างาน' : 'ออกงาน'}</span>
                          </div>
                          <div className="flex items-center justify-center space-x-2">
                            <Clock className="h-4 w-4" />
                            <span className="text-sm">{new Date().toLocaleString('th-TH')}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                <button
                  onClick={() => {
                    setResult(null);
                    startCamera();
                  }}
                  className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-4 px-6 rounded-2xl font-bold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center space-x-3"
                >
                  <Camera className="h-6 w-6" />
                  <span>กดเพื่อสแกนอีกครั้ง</span>
                </button>
              </div>
            </div>
          )}
        </div>
        
        {!location.loading && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-white/20">
            <div className="flex items-center space-x-3 text-sm">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <MapPin className="h-4 w-4 text-blue-600" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900">Current Location</p>
                <p className="text-gray-600 truncate">
                  {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
                  <span className="text-gray-500"> (±{Math.round(location.accuracy)}m)</span>
                </p>
              </div>
              <div className="flex-shrink-0">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="fixed bottom-4 right-4 bg-gray-900/90 text-white px-3 py-1.5 rounded-full text-xs font-semibold shadow-2xl z-50">
        {CURRENT_VERSION}
      </div>
    </div>
  );
};

export default FaceScanner;